import { crossdockingHubSignalrConnector } from './crossdockingHubSignalrConnector';
import { locationHubSignalrConnector } from './locationHubSignalrConnector';
/**
 * Builds an `onCacheEntryAdded` handler that owns the whole subscription lifecycle:
 * it waits for the cache entry to load, subscribes, and detaches every listener once the entry is gone.
 *
 * Endpoints only describe which event maps to which handler, so a subscription can never be left behind.
 *
 * @typeParam TData - The endpoint's result type, the first generic of `builder.query`.
 * @typeParam TQueryArgument - The endpoint's argument type, the second generic of `builder.query`.
 * Pass it only when a handler actually reads `queryArgument`; otherwise it stays `unknown`.
 */
const createHubCacheListener = (connector) => (createListeners) => async (queryArgument, api) => {
    const { addEventListener } = connector();
    const unsubscribes = [];
    const subscribe = (eventName, listeners) => {
        const listener = listeners[eventName];
        if (listener)
            unsubscribes.push(addEventListener(eventName, listener));
    };
    try {
        // Wait for the initial query to resolve before proceeding.
        await api.cacheDataLoaded;
        const listeners = createListeners(api, queryArgument);
        Object.keys(listeners).forEach((eventName) => subscribe(eventName, listeners));
    }
    catch {
        // No-op in case `cacheEntryRemoved` resolves before `cacheDataLoaded`,
        // in which case `cacheDataLoaded` will throw.
    }
    // `cacheEntryRemoved` will resolve when the cache subscription is no longer active.
    await api.cacheEntryRemoved;
    unsubscribes.forEach((unsubscribe) => unsubscribe());
};
// The connectors are referenced lazily: reading them at module evaluation time breaks on circular imports.
export const createCrossdockingHubCacheListener = createHubCacheListener(() => crossdockingHubSignalrConnector());
export const createLocationHubCacheListener = createHubCacheListener(() => locationHubSignalrConnector());
